import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Calendar, BookmarkCheck, History, Book, Check, Star,
  Share2, Sparkles, Volume2, Pause, Heart,
} from "lucide-react";
import { fetchHome } from "../lib/api";
import { api as apiClient } from "../lib/api";
import { getReadingProgress, getReadingHistory, getActivePlan, formatProgressSubtitle, labelForType } from "../lib/readingProgress";
import {
  isTodayCompleted, markTodayCompleted, unmarkTodayCompleted,
  isFavoriteDevotional, toggleFavoriteDevotional,
  shareContent, getActionOfTheDay, getDailyConnections, fetchAIConnections,
  getStreakMessage, getCompletionFeedback,
  getCompletionsCount, getCompletedPlansCount,
  getCurrentStreak, getLongestStreak, getWeekCompletion,
} from "../lib/dailyJourney";
import { getDailyDevotional } from "../lib/dailyContent";
import useDaily from "../lib/useDaily";
import { dailyToConnections } from "../lib/dailyApi";
import useTTS from "../lib/useTTS";
import JournalPrompt from "../components/JournalPrompt";
import DailyAIReflectionCard from "../components/DailyAIReflectionCard";
import { useAuth } from "../lib/authContext";
import PrayerWeekCard from "../components/PrayerWeekCard";
import StreakVisual from "../components/StreakVisual";
import TodayChallengePill from "../components/TodayChallengePill";
import PalavraMinutoCard from "../components/PalavraMinutoCard";
import { recordEvent, useScrollDepth } from "../lib/retention";
import { BibleRefLink } from "../lib/bibleRef";
import { filterList, isRenderable } from "../lib/editorialFilter";
import { Skeleton } from "../components/ui/skeleton";
import { ScriptureConnections } from "../components/ScriptureConnections";
import { LiturgicalBadge, JourneyDayLabel } from "../components/LiturgicalBadge";
import { WordThatRemainsCard, JourneyMilestoneCard } from "../components/DevotionalSideCards";
import { InstallAppButton } from "../components/InstallAppButton";

// Saudação dinâmica pastoral (Fase A — iter64) conforme Prompt Master aprovado.
// Retorna objeto {header, subtitle} para renderização em duas linhas curtas.
function greetingBlock() {
  const h = new Date().getHours();
  if (h < 12) {
    return {
      header: "Bom dia!",
      subtitle:
        "Que a paz do Senhor esteja com você. Deus preparou uma Palavra para o seu coração hoje.",
    };
  }
  if (h < 18) {
    return {
      header: "Boa tarde!",
      subtitle:
        "Que a graça e a paz do Senhor estejam sobre a sua vida. Mesmo em meio às atividades, Deus deseja falar ao seu coração.",
    };
  }
  return {
    header: "Boa noite!",
    subtitle:
      "Que a paz do Senhor envolva o seu coração. Antes de descansar, entregue ao Senhor suas preocupações e permita que a Palavra renove suas forças.",
  };
}

export default function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [hasHistory, setHasHistory] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [favDevo, setFavDevo] = useState(false);
  const [streakInfo, setStreakInfo] = useState(null);
  const [stats, setStats] = useState({ total: 0, plans: 0 });
  const [connections, setConnections] = useState(() => getDailyConnections());
  // Fonte unificada de conteúdo diário (landing /api/daily). Falha silenciosa.
  const { daily } = useDaily();
  const navigate = useNavigate();
  const tts = useTTS();
  const { isAuthenticated } = useAuth();

  // Sprint refinamento — auditoria silenciosa de retenção.
  // Fire-and-forget: view + scroll depth 25/50/75/100. Nunca altera UX.
  useScrollDepth("home");

  const refreshHabit = () => {
    setStreakInfo(getStreakMessage());
    setStats({ total: getCompletionsCount(), plans: getCompletedPlansCount() });
  };

  useEffect(() => {
    fetchHome()
      .then((d) => {
        setData(d);
        const todayDevo = getDailyDevotional({ upstream: d?.devotional });
        setFavDevo(isFavoriteDevotional(todayDevo?.id));
      })
      .finally(() => setLoading(false));
    setProgress(getReadingProgress());
    setHasHistory(getReadingHistory().length > 0);
    setActivePlan(getActivePlan());
    setCompleted(isTodayCompleted());
    refreshHabit();
  }, []);

  // Deterministic daily rotation, uses landing /api/daily (single source of
  // truth) when available, otherwise /api/home upstream, otherwise the
  // locally-curated 7-day pastoral rotation.
  // The daily payload from the landing has a different schema (title +
  // lead_paragraph + prayer + central_verse), we map it to the Home devo
  // shape so the existing UI keeps rendering without changes.
  const devo = (() => {
    if (daily?.devotional?.title || daily?.central_verse?.text) {
      const today = new Date();
      const dateIso = daily.date || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      return {
        id: `daily-${dateIso}`,
        title: daily.devotional?.title || "",
        verse_ref: daily.central_verse?.reference || "",
        verse_text: daily.central_verse?.text || "",
        reflection: daily.pastoral_blocks?.abertura || "",
        impact_phrase: daily.phrase || "",
        prayer: daily.prayer || "",
        application: "",
        published: true,
        _source: "landing-daily",
      };
    }
    return data ? getDailyDevotional({ upstream: data.devotional }) : null;
  })();
  const action = getActionOfTheDay(devo);

  // AI connections: prefer landing /api/daily related_chapters when available;
  // otherwise call /api/assistant/connections; otherwise fall back to the static
  // weekday rotation that was the original implementation.
  useEffect(() => {
    let cancelled = false;
    const fromDaily = dailyToConnections(daily);
    if (fromDaily.length) {
      if (!cancelled) setConnections(fromDaily);
      return () => { cancelled = true; };
    }
    fetchAIConnections(apiClient).then((items) => {
      if (!cancelled && Array.isArray(items) && items.length) setConnections(items);
    });
    return () => { cancelled = true; };
  }, [daily]);

  const onTTSDevoClick = () => {
    if (!devo) return;
    if (tts.playing && !tts.paused) { tts.pause(); return; }
    if (tts.playing && tts.paused) { tts.resume(); return; }
    const items = [];
    if (devo.title) items.push({ id: "title", text: devo.title });
    if (devo.verse_text) items.push({ id: "verse", text: devo.verse_text });
    if (devo.reflection) items.push({ id: "reflection", text: devo.reflection });
    if (action) items.push({ id: "action", text: action });
    tts.speak(items);
  };

  const handleComplete = () => {
    if (completed) {
      unmarkTodayCompleted();
      setCompleted(false);
      refreshHabit();
      toast("Você pode refazer o devocional quando quiser.");
    } else {
      markTodayCompleted();
      setCompleted(true);
      refreshHabit();
      const fb = getCompletionFeedback();
      toast.success(fb.title, { description: fb.description });
    }
  };

  const handleFavorite = () => {
    if (!devo?.id) return;
    const nowFav = toggleFavoriteDevotional({
      id: devo.id, title: devo.title, verse_ref: devo.verse_ref,
    });
    setFavDevo(nowFav);
    toast(nowFav ? "Salvo nos favoritos." : "Removido dos favoritos.");
  };

  const handleShare = async () => {
    if (!devo) return;
    const excerpt = (devo.reflection || "").slice(0, 180);
    const res = await shareContent({
      title: `Devocional · ${devo.title}`,
      text: `"${devo.verse_ref}", ${excerpt}${excerpt.length === 180 ? "…" : ""}`,
      url: window.location.origin + "/devocional",
    });
    if (res === "copied") toast.success("Link copiado para compartilhar.");
    else if (res === "shared") toast.success("Compartilhado!");
    else if (res === "failed") toast.error("Não foi possível compartilhar agora.");
  };

  const handleShareAction = async () => {
    const res = await shareContent({
      title: "Prática do dia",
      text: action,
      url: window.location.origin,
    });
    if (res === "copied") toast.success("Prática copiada para compartilhar.");
    else if (res === "shared") toast.success("Compartilhado!");
    else if (res === "failed") toast.error("Não foi possível compartilhar agora.");
  };

  const handleShareConnection = async (c) => {
    const absHref = c.available && c.href ? window.location.origin + c.href : window.location.origin;
    const res = await shareContent({
      title: `Conexão bíblica · ${c.ref}`,
      text: `Leia ${c.ref} hoje.`,
      url: absHref,
    });
    if (res === "copied") toast.success("Passagem copiada para compartilhar.");
    else if (res === "shared") toast.success("Compartilhado!");
    else if (res === "failed") toast.error("Não foi possível compartilhar agora.");
  };

  return (
    <div data-testid="home-page">
      {/* Hero / greeting — pública, minimal, conduz ao Devocional. */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 bg-[hsl(var(--navy))]" />
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_20%_10%,hsl(var(--gold))_0%,transparent_50%)]" />
        <div className="relative max-w-5xl mx-auto section-padding pt-10 pb-16 text-white">
          {/* Saudação dinâmica pastoral (Fase A — iter64). Curta, tom pastoral,
              sem menção a IA, sem frases genéricas. Conduz direto ao Devocional. */}
          {(() => { const g = greetingBlock(); return (
            <>
              <p className="text-xs uppercase tracking-[0.3em] text-gold fade-in" data-testid="home-greeting">
                {g.header}
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] fade-in-delay-1">
                Deus preparou uma <em className="text-gold not-italic">Palavra</em><br className="hidden sm:block" />
                para o seu coração hoje.
              </h1>
              <p className="mt-5 text-white/75 max-w-xl fade-in-delay-2" data-testid="home-greeting-subtitle">
                {g.subtitle}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3 fade-in-delay-2">
                <Link
                  to="/devocional"
                  data-testid="home-open-devotional-primary"
                  onClick={() => recordEvent("click", "devocional-open", { source: "home-primary-cta" })}
                  className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:bg-white transition-colors"
                >
                  Abrir Devocional de Hoje <ArrowRight className="w-4 h-4" />
                </Link>
                <InstallAppButton />
              </div>
            </>
          ); })()}
        </div>
      </section>

      {/* Devocional do Dia — CONTEÚDO PRINCIPAL da Home pública. */}
      <section className="max-w-5xl mx-auto section-padding -mt-10 relative z-10">
        {/* Fase A (iter64) — CommunityStatsStrip removido: contadores hardcoded
            saem da Home pública. Voltam na Fase C com métricas reais.
            QuickAccessGrid removido: duplicava a bottom nav.
            DailyAIReflectionCard reposicionado para APÓS PalavraMinuto,
            ocupando o slot "Versículo do dia" conforme spec. */}
        {loading ? (
          <Skeleton className="h-80 rounded-2xl" />
        ) : devo ? (
          <article
            data-testid="home-devotional-card"
            className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden"
          >
            {/* Header strip */}
            <div className="bg-[hsl(var(--parchment))] px-6 sm:px-8 pt-6 pb-5 flex items-center justify-between gap-4">
              <div>
                <p data-testid="home-devotional-eyebrow" className="text-[10px] uppercase tracking-[0.3em] text-navy/60">Palavra do Dia</p>
                <h2
                  className="font-display text-3xl sm:text-4xl text-navy mt-2 leading-[1.1]"
                  data-testid="home-devotional-title"
                >
                  {devo.title}
                </h2>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={handleFavorite}
                  aria-label={favDevo ? "Remover favorito" : "Salvar favorito"}
                  data-testid="home-devotional-favorite"
                  data-active={favDevo ? "true" : "false"}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                    favDevo ? "bg-gold text-navy" : "bg-white border border-border text-navy/60 hover:text-gold"
                  }`}
                >
                  <Star className="w-4 h-4" fill={favDevo ? "currentColor" : "none"} />
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  aria-label="Compartilhar devocional"
                  data-testid="home-devotional-share"
                  className="w-9 h-9 rounded-full bg-white border border-border flex items-center justify-center text-navy/60 hover:text-gold transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                {tts.supported && (
                  <button
                    type="button"
                    onClick={onTTSDevoClick}
                    aria-label={tts.playing && !tts.paused ? "Pausar" : "Ouvir devocional"}
                    data-testid="home-devotional-tts"
                    data-state={tts.playing ? (tts.paused ? "paused" : "playing") : "idle"}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors border ${
                      tts.playing
                        ? "bg-gold border-gold text-navy"
                        : "bg-white border-border text-navy/60 hover:text-gold"
                    }`}
                  >
                    {tts.playing && !tts.paused ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 sm:px-8 py-7 space-y-7">
              {/* Grid Palavra do Dia: esquerda identidade e mensagem, direita
                  cartões autorais. Mobile empilha na ordem identidade,
                  Palavra que Permanece, Marco da Jornada, mensagem. */}
              <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10">
                <div className="space-y-7 min-w-0">
                  {/* Bible passage */}
                  {(devo.verse_ref || devo.verse_text) && (
                    <div data-testid="home-devotional-verse">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Passagem</p>
                      {devo.verse_text && (
                        <blockquote className="font-display text-xl sm:text-2xl text-navy mt-2 leading-snug italic">
                          “{devo.verse_text}”
                        </blockquote>
                      )}
                      {devo.verse_ref && (
                        <BibleRefLink
                          refText={devo.verse_ref}
                          testid="home-devotional-verse-ref-link"
                          className="mt-2 inline-block text-sm text-gold font-medium tracking-wide hover:underline hover:text-navy transition-colors"
                        />
                      )}
                    </div>
                  )}

                  {/* Entrada única e serena para a leitura completa. */}
                  <Link
                    to="/devocional"
                    data-testid="home-devotional-start"
                    onClick={() => recordEvent("click", "devocional-open", { source: "home-card-start" })}
                    className="inline-flex items-center gap-2 rounded-full bg-navy text-white hover:bg-gold hover:text-navy px-6 py-3 text-[11px] uppercase tracking-widest transition-colors"
                  >
                    Começar agora <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <aside className="space-y-5 lg:row-span-2" data-testid="home-side-cards">
                  <WordThatRemainsCard
                    phrase={daily?.pastoral_blocks?.frase_compartilhar || devo.impact_phrase}
                    verseRef={devo.verse_ref}
                  />
                  <JourneyMilestoneCard
                    cycleDay={daily?.cycle_day}
                    dateIso={daily?.date}
                    weeklyTheme={daily?.weekly_theme}
                  />
                </aside>

                <div className="space-y-7 min-w-0 lg:col-start-1">
                  {/* Reflection */}
                  {devo.reflection && (
                    <div data-testid="home-devotional-reflection">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Reflexão</p>
                      <p className="mt-2 text-navy/85 text-[15px] sm:text-base leading-[1.75] whitespace-pre-wrap devotional-justify">
                        {devo.reflection}
                      </p>
                    </div>
                  )}

                  {/* Prayer */}
                  {devo.prayer && (
                    <div data-testid="home-devotional-prayer">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Oração</p>
                      <p className="mt-2 text-navy/85 italic text-[15px] sm:text-base leading-[1.75] whitespace-pre-wrap">
                        {devo.prayer}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Conexões bíblicas canônicas (contrato v1.1), agrupadas por
                  função teológica. Silêncio elegante quando ausentes. */}
              {Array.isArray(daily?.scripture_connections) && daily.scripture_connections.length > 0 && (
                <div data-testid="home-biblical-connection" className="pt-2">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Vá além, leia também</p>
                  <ScriptureConnections
                    connections={daily.scripture_connections}
                    testidPrefix="home-scripture-connection"
                  />
                </div>
              )}

              {/* Plano sugerido do /api/daily (item 6 do pacote diário).
                  P0.3: só renderiza se o slug sugerido existe na lista de planos
                  publicados (data.plans já filtrada). Evita link 404. */}
              {daily?.related_plan?.title && daily?.related_plan?.slug && (data?.plans || []).some((p) => p.slug === daily.related_plan.slug) && (
                <Link
                  to={`/planos/${daily.related_plan.slug}`}
                  data-testid="home-suggested-plan"
                  className="block rounded-2xl border border-navy/15 bg-white p-5 hover:border-gold/60 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Plano sugerido</p>
                      <p
                        className="font-display text-lg sm:text-xl text-navy leading-snug"
                        data-testid="home-suggested-plan-title"
                      >
                        {daily.related_plan.title}
                      </p>
                      {daily.related_plan.subtitle && (
                        <p className="mt-1.5 text-sm text-navy/65 leading-relaxed">
                          {daily.related_plan.subtitle}
                        </p>
                      )}
                      <div className="mt-3 flex items-center gap-3 text-[11px] uppercase tracking-widest text-navy/55">
                        {daily.related_plan.duration_days ? (
                          <span>{daily.related_plan.duration_days} dias</span>
                        ) : null}
                        {daily.related_plan.category ? (
                          <span>· {daily.related_plan.category}</span>
                        ) : null}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gold shrink-0 mt-1" />
                  </div>
                </Link>
              )}

              {/* Próximo passo, no máximo duas sugestões, nunca lista extensa.
                  Convite sereno para o tema da semana, sem linguagem de cobrança. */}
              {daily?.weekly_theme?.theme_name && (
                <Link
                  to="/semana"
                  data-testid="home-next-step-semana"
                  className="flex items-center justify-between gap-3 rounded-2xl border border-navy/15 bg-white p-5 hover:border-gold/60 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1.5">Próximo passo</p>
                    <p className="font-display text-lg text-navy leading-snug truncate">
                      {daily.weekly_theme.theme_name}
                    </p>
                    <p className="mt-1 text-sm text-navy/60">
                      Caminhe pelo tema desta semana, no seu ritmo.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gold shrink-0" />
                </Link>
              )}

              {/* Action of the day */}
              <div
                data-testid="home-action-of-day"
                className="rounded-2xl border border-gold/40 bg-[hsl(var(--parchment))] p-5"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-gold" />
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Prática do dia</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleShareAction}
                    aria-label="Compartilhar prática do dia"
                    data-testid="home-action-of-day-share"
                    className="w-7 h-7 rounded-full bg-white/80 border border-gold/30 flex items-center justify-center text-navy/60 hover:text-gold transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p
                  className="mt-2 font-display text-lg sm:text-xl text-navy leading-snug"
                  data-testid="home-action-of-day-text"
                >
                  {action}
                </p>
              </div>

              {/* Completion */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                <Link
                  to="/devocional"
                  data-testid="home-devotional-read-full"
                  onClick={() => recordEvent("click", "devocional-open", { source: "home" })}
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/60 hover:text-gold transition-colors"
                >
                  Abrir devocional na íntegra <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    recordEvent("click", "devocional-complete", { source: "home" });
                    handleComplete();
                  }}
                  data-testid="home-devotional-complete"
                  data-completed={completed ? "true" : "false"}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
                    completed
                      ? "bg-gold text-navy"
                      : "bg-navy text-white hover:bg-gold hover:text-navy"
                  }`}
                >
                  <Check className="w-3.5 h-3.5" />
                  {completed ? "Concluído hoje" : "Concluir devocional de hoje"}
                </button>
              </div>
            </div>
          </article>
        ) : null}

      {/* Reflexão em Vídeo (P0.4A) — labels adaptados por source
          (devotional / manual_override / evergreen). */}
      {isRenderable(data?.featured_devotional_video || data?.featured_youtube_video, "youtubeVideo") && (() => {
        const fv = data.featured_devotional_video || data.featured_youtube_video;
        const source = fv.source || "devotional";
        const labels =
          source === "manual_override"
            ? {
                heading: "Mensagem Especial",
                subtitle: fv.override_reason
                  ? `Mensagem especial de ${fv.override_reason}.`
                  : "Uma mensagem pastoral especialmente selecionada para este momento.",
              }
            : source === "evergreen"
            ? {
                heading: "Reflexão em Vídeo",
                subtitle: "Uma mensagem para fortalecer sua fé hoje.",
              }
            : {
                heading: "Reflexão em Vídeo",
                subtitle: "Assista a uma mensagem relacionada ao Devocional de Hoje.",
              };
        const watchUrl = fv.youtube_url || `https://youtube.com/watch?v=${fv.video_id || fv.youtube_video_id}`;
        const videoId = fv.video_id || fv.youtube_video_id;
        const shareText = `Assista: ${fv.title}`;
        const handleShareVideo = async () => {
          try {
            if (navigator.share) {
              await navigator.share({ title: fv.title, text: shareText, url: watchUrl });
              recordEvent("click", "featured-video-share", { video_id: videoId, source });
            } else {
              await navigator.clipboard.writeText(watchUrl);
              toast.success("Link do vídeo copiado.");
            }
          } catch { /* silent */ }
        };
        return (
          <section className="max-w-5xl mx-auto section-padding mt-6" data-testid="home-reflexao-video-section" data-source={source}>
            <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-sm" data-testid="home-reflexao-video-card">
              <div className="px-5 sm:px-6 pt-5 sm:pt-6">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Assista</p>
                <h2 className="font-display text-xl sm:text-2xl text-navy mt-1 leading-snug line-clamp-2" data-testid="home-reflexao-video-title">
                  {labels.heading}
                </h2>
                <p className="text-sm text-navy/60 mt-1" data-testid="home-reflexao-video-subtitle">
                  {labels.subtitle}
                </p>
              </div>
              <div className="relative aspect-video mt-4">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title={fv.title}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="home-reflexao-video-iframe"
                />
              </div>
              <div className="p-5 sm:p-6">
                <p className="font-medium text-navy leading-snug line-clamp-2" data-testid="home-reflexao-video-vtitle">{fv.title}</p>
                {fv.description && (
                  <p className="mt-1.5 text-sm text-navy/75 leading-relaxed line-clamp-3" data-testid="home-reflexao-video-vdesc">
                    {fv.description}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <a
                    href={watchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => recordEvent("click", "featured-video-watch", { video_id: videoId, source: fv.source })}
                    data-testid="home-reflexao-video-watch"
                    className="inline-flex items-center gap-2 min-h-[44px] bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                  >
                    Assistir agora <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={handleShareVideo}
                    data-testid="home-reflexao-video-share"
                    aria-label="Compartilhar Reflexão em Vídeo"
                    className="inline-flex items-center gap-2 min-h-[44px] border border-border text-navy/70 hover:text-gold hover:border-gold/60 px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Compartilhar
                  </button>
                  <a
                    href={watchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="home-reflexao-video-open"
                    className="ml-auto text-[11px] uppercase tracking-widest text-navy/55 hover:text-gold transition-colors"
                  >
                    Ver no YouTube
                  </a>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Palavra em 1 Minuto — N1/final: entre Reflexão em Vídeo e Bíblia.
          Auto-oculta quando não há conteúdo publicado (silêncio elegante). */}
      <PalavraMinutoCard />

        {/* Versículo do dia — DailyAIReflectionCard.
            Anônimo: verso rotativo + CTA para cadastro.
            Autenticado: reflexão personalizada via IA.
            N1: movida para logo APÓS Tema da Semana (posição de "Auxílio
            Bíblico" provisório). Ver bloco correspondente abaixo. */}

        {/* Desafio de hoje (defensivo, aparece somente quando landing publicar). */}
        {devo && !loading && (
          <TodayChallengePill />
        )}

        {/* Journal prompt — recurso PESSOAL, autenticado apenas.
            Home só renderiza para anônimos (HomeOrDashboard route),
            então este bloco é uma rede de segurança defensiva. */}
        {isAuthenticated && devo && !loading && (
          <JournalPrompt
            refKey={`devo-${devo.id || "today"}`}
            refLabel={`${devo.title || "Devocional"}, ${new Date().toLocaleDateString("pt-BR")}`}
            type="devotional"
            devotionalId={devo.id || null}
          />
        )}
      </section>

      {/* Bíblia — bloco editorial público, CTA para leitura das escrituras. */}
      <section className="max-w-5xl mx-auto section-padding mt-10" data-testid="home-bible-section">
        <Link
          to="/biblia"
          data-testid="home-bible-cta"
          onClick={() => recordEvent("click", "bible-open", { source: "home-editorial" })}
          className="group relative block overflow-hidden rounded-2xl bg-[hsl(var(--parchment))] border border-gold/30 hover:border-gold transition-colors"
        >
          <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_85%_20%,hsl(var(--gold))_0%,transparent_50%)]" />
          <div className="relative p-6 sm:p-8 flex items-center gap-5">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm">
              <Book className="w-6 h-6 sm:w-7 sm:h-7 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">A Palavra</p>
              <h2 className="font-display text-2xl sm:text-3xl text-navy mt-1 leading-snug">
                Leia a Bíblia Sagrada
              </h2>
              <p className="mt-1.5 text-sm text-navy/70 leading-relaxed">
                Antigo e Novo Testamento em Almeida Revista e Corrigida, com busca e leitura por capítulo.
              </p>
            </div>
            <span className="hidden sm:inline-flex shrink-0 items-center gap-2 bg-navy text-white group-hover:bg-gold group-hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors">
              Abrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      </section>

      {/* N1 — Tema da Semana (SOMENTE o dia atual). Movido para APÓS Bíblia,
          antes de Planos. Anti-mistura interno já ativo. Auto-oculta quando
          não há dia correspondente a hoje ou landing publica tema desalinhado. */}
      {!loading && (
        <div className="max-w-5xl mx-auto section-padding" data-testid="home-prayer-week-today">
          <PrayerWeekCard todayOnly />
        </div>
      )}

      {/* Reflexão do Auxílio Bíblico — slot editorial provisório (N1).
          Enquanto N2 (bloco editorial cacheado) não estiver pronto, usa o
          DailyAIReflectionCard existente: verso rotativo (anônimo) ou reflexão
          IA personalizada (autenticado). Silêncio elegante interno se nada
          retornar. */}
      <div className="max-w-5xl mx-auto section-padding mt-8" data-testid="home-auxilio-biblico-slot">
        <DailyAIReflectionCard />
      </div>

      {/* Planos de Leitura — bloco editorial público, vem logo após Bíblia.
          Filtro editorial (P0.3): planos sem dias publicados ou incompletos
          nunca aparecem, tanto no backend (/api/home) quanto aqui. */}
      {filterList(data?.plans, "plan").length > 0 && (
        <section className="max-w-5xl mx-auto section-padding mt-10" data-testid="home-plans-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Jornada</p>
              <h2 className="font-display text-3xl text-navy mt-1">Planos de Leitura</h2>
            </div>
            <Link
              to="/planos"
              className="text-xs uppercase tracking-widest text-navy/70 hover:text-gold flex items-center gap-1"
              data-testid="home-planos-all"
            >
              Ver todos <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filterList(data.plans, "plan").map((p) => (
              <Link
                key={p.id}
                to={`/planos/${p.slug}`}
                data-testid={`home-plan-${p.slug}`}
                className="group relative overflow-hidden rounded-2xl card-lift border border-border"
                style={{ background: p.cover_color || "#0B1E3F" }}
              >
                <div className="absolute inset-0 opacity-[0.09] bg-[radial-gradient(circle_at_85%_20%,hsl(var(--gold))_0%,transparent_45%)]" />
                <div className="relative p-6 text-white flex flex-col min-h-[180px]">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Plano</span>
                    <span className="text-xs text-white/70">{p.duration_days} dias</span>
                  </div>
                  <h3 className="font-display text-xl mt-4 leading-tight">{p.title}</h3>
                  <p className="text-xs text-white/70 mt-2 line-clamp-2">{p.description}</p>
                  <div className="mt-auto pt-4 flex items-center gap-2 text-[11px] uppercase tracking-widest text-gold">
                    Começar <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* NOTA (M4): Sermão em Destaque foi movido para APÓS o bloco de Livros
          conforme ordem editorial aprovada:
          Devocional → Vídeo → Versículo → Semana → Bíblia → Planos → LIVROS →
          Sermão → Palavra em 1 Minuto → Oração. Ver bloco abaixo. */}

      {/* Vídeo em Destaque do YouTube — REMOVIDO da posição 9 pelo P0.4A.
          Agora renderizado como "Reflexão em Vídeo" imediatamente após o
          Devocional (posição 3). Ver bloco `home-reflexao-video-section`
          na seção do Devocional acima. */}

      {/* Livro do Dia + carrossel complementar — rotação diária determinística
          por America/Recife (backend calcula book_of_the_day; frontend usa `books`
          como carrossel secundário). Retrocompat: se a API só retornar `books`,
          cai no primeiro livro renderizável. */}
      {(() => {
        const bod = data?.book_of_the_day;
        const fallback = (data?.books || []).find((b) => isRenderable(b, "book"));
        const book = bod || fallback;
        if (!book) return null;
        const buyHref = book.buy_url || `/livros/${book.slug}`;
        const others = (data?.books || []).filter((b) => b && b.slug !== book.slug && isRenderable(b, "book"));
        return (
          <section className="max-w-5xl mx-auto section-padding mt-12" data-testid="home-book-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Biblioteca · Livro do dia</p>
                <h2 className="font-display text-3xl text-navy mt-1">Uma leitura para hoje</h2>
              </div>
              <Link
                to="/livros"
                className="text-xs uppercase tracking-widest text-navy/70 hover:text-gold flex items-center gap-1"
                data-testid="home-book-all"
              >
                Ver todos <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Card principal — livro do dia */}
            <div
              data-testid="home-book-card"
              data-book-slug={book.slug}
              className="grid gap-6 sm:gap-8 sm:grid-cols-[220px_1fr] items-start bg-white rounded-2xl border border-border shadow-sm p-6 sm:p-8"
            >
              <a
                href={buyHref}
                target={book.buy_url ? "_blank" : undefined}
                rel={book.buy_url ? "noopener noreferrer" : undefined}
                data-testid="home-book-cover"
                className="block w-full max-w-[220px] mx-auto sm:mx-0"
                onClick={() => recordEvent("click", "book-cover", { slug: book.slug, source: "book_of_the_day" })}
              >
                <img
                  src={book.cover_url}
                  alt={`Capa do livro ${book.title}`}
                  className="w-full aspect-[2/3] object-cover rounded-xl shadow-md"
                  loading="lazy"
                />
              </a>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{book.category || "Livro"}</p>
                <h3
                  className="font-display text-2xl sm:text-3xl text-navy mt-1.5 leading-tight"
                  data-testid="home-book-title"
                >
                  {book.title}
                </h3>
                {book.subtitle && (
                  <p className="mt-1.5 text-sm text-navy/70 leading-relaxed">{book.subtitle}</p>
                )}
                {book.description && (
                  <p
                    className="mt-3 text-[15px] text-navy/85 leading-relaxed line-clamp-4"
                    data-testid="home-book-description"
                  >
                    {book.description}
                  </p>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a
                    href={buyHref}
                    target={book.buy_url ? "_blank" : undefined}
                    rel={book.buy_url ? "noopener noreferrer" : undefined}
                    data-testid="home-book-cta"
                    onClick={() => recordEvent("click", "book-cta", { slug: book.slug, source: "book_of_the_day" })}
                    className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                  >
                    Conhecer livro <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                  <Link
                    to="/livros"
                    data-testid="home-book-all-link"
                    className="text-[11px] uppercase tracking-widest text-navy/60 hover:text-gold transition-colors"
                  >
                    Ver todos os livros
                  </Link>
                </div>
              </div>
            </div>

            {/* Carrossel complementar — livros restantes, swipe horizontal no mobile */}
            {others.length > 0 && (
              <div className="mt-6" data-testid="home-book-carousel">
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin">
                  {others.map((b) => {
                    const href = b.buy_url || `/livros/${b.slug}`;
                    return (
                      <a
                        key={b.slug}
                        href={href}
                        target={b.buy_url ? "_blank" : undefined}
                        rel={b.buy_url ? "noopener noreferrer" : undefined}
                        data-testid={`home-book-carousel-item-${b.slug}`}
                        onClick={() => recordEvent("click", "book-carousel-item", { slug: b.slug })}
                        className="shrink-0 snap-start w-[140px] sm:w-[160px] group"
                      >
                        <div className="aspect-[2/3] rounded-lg overflow-hidden border border-border bg-[hsl(var(--parchment))]">
                          <img
                            src={b.cover_url}
                            alt={`Capa do livro ${b.title}`}
                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                            loading="lazy"
                          />
                        </div>
                        <p className="mt-2 text-[13px] font-medium text-navy leading-snug line-clamp-2">
                          {b.title}
                        </p>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Mensagem pastoral de apoio — versão curta, discreta */}
            <p
              className="mt-5 text-[13px] text-navy/60 italic leading-relaxed max-w-2xl"
              data-testid="home-book-support-message"
            >
              Sua leitura também pode se transformar em missão. Ao adquirir um livro,
              você ajuda esta obra a continuar alcançando vidas.
            </p>
          </section>
        );
      })()}

      {/* Sermão em Destaque — M4: movido para APÓS Livros conforme ordem editorial aprovada. */}
      {filterList(data?.sermons, "sermon").length > 0 && (
        <section className="max-w-5xl mx-auto section-padding mt-12" data-testid="home-sermon-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Biblioteca</p>
              <h2 className="font-display text-3xl text-navy mt-1">Sermão em destaque</h2>
            </div>
            <Link
              to="/sermoes"
              className="text-xs uppercase tracking-widest text-navy/70 hover:text-gold flex items-center gap-1"
              data-testid="home-sermoes-all"
            >
              Ver todos <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filterList(data.sermons, "sermon").map((s) => (
              <Link
                key={s.id}
                to={`/sermoes/${s.id}`}
                data-testid={`home-sermon-${s.id}`}
                className="block bg-white border border-border rounded-2xl p-5 card-lift"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{s.category}</div>
                <h3 className="font-display text-xl text-navy mt-2 leading-snug line-clamp-2">
                  {s.title}
                </h3>
                <p className="text-xs text-navy/60 mt-2">{s.base_text}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Palavra em 1 Minuto — N1/final: MOVIDA para logo após "Reflexão em
          Vídeo" e antes da Bíblia (ver bloco `PalavraMinutoCard` acima). */}

      {/* Pedido de Oração — bloco editorial público. Envio inicial permanece
          público (sem exposição de dados pessoais). */}
      <section className="max-w-5xl mx-auto section-padding mt-12" data-testid="home-prayer-request-section">
        <div className="rounded-2xl bg-[hsl(var(--parchment))] border border-gold/30 p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm">
              <Heart className="w-5 h-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Pedido de Oração</p>
              <h2 className="font-display text-2xl sm:text-3xl text-navy mt-1 leading-snug">
                Podemos orar por você
              </h2>
              <p className="mt-2 text-[15px] text-navy/75 leading-relaxed max-w-xl">
                Confie ao Senhor a sua causa. Envie seu pedido e nossa equipe pastoral orará por você em sigilo.
              </p>
              <Link
                to="/oracao"
                data-testid="home-prayer-request-cta"
                onClick={() => recordEvent("click", "prayer-request-open", { source: "home" })}
                className="mt-4 inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
              >
                Enviar pedido de oração <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Continue sua jornada conosco — Entrar / Criar Conta.
          Bloco de encerramento da Home. SÓ para usuários anônimos. */}
      {!isAuthenticated && (
        <section
          className="max-w-5xl mx-auto section-padding mt-12"
          data-testid="home-auth-cta-section"
        >
          <div className="rounded-2xl bg-[hsl(var(--navy))] border border-gold/30 p-7 sm:p-10 text-white">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Continue sua jornada conosco</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">
              Sua caminhada com Deus, guardada em qualquer lugar.
            </h2>
            <p className="mt-3 text-white/75 max-w-xl leading-relaxed">
              Entre para acompanhar sua jornada, registrar seu progresso e acessar seus recursos pessoais.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/cadastro"
                data-testid="home-auth-cta-cadastro"
                onClick={() => recordEvent("click", "cadastro", { source: "home-final-cta" })}
                className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-white px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
              >
                Criar conta <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/entrar"
                data-testid="home-auth-cta-entrar"
                onClick={() => recordEvent("click", "entrar", { source: "home-final-cta" })}
                className="inline-flex items-center gap-2 border border-white/25 text-white hover:border-gold hover:text-gold px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
              >
                Entrar
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Blocos pessoais autenticados (defensive net — Home só é servida
          para anônimos; auth users vão para DashboardLogado). */}
      {isAuthenticated && streakInfo && (
        <section className="max-w-5xl mx-auto section-padding mt-10" data-testid="home-streak">
          <div
            data-testid="home-streak-card"
            data-tone={streakInfo.tone}
            className="rounded-2xl px-5 py-5 sm:px-6 sm:py-6 bg-[hsl(var(--navy))] border border-gold/30"
          >
            <StreakVisual
              current={getCurrentStreak()}
              longest={getLongestStreak()}
              week={getWeekCompletion()}
              variant="dark"
              eyebrow="Sua ofensiva"
              testid="home-streak-visual"
            />
            {(stats.total > 0 || stats.plans > 0) && (
              <p
                className="mt-4 pt-4 border-t border-white/10 text-[11px] uppercase tracking-[0.2em] text-gold/80"
                data-testid="home-streak-stats"
              >
                {stats.total} {stats.total === 1 ? "dia" : "dias"} concluído{stats.total === 1 ? "" : "s"}
                {stats.plans > 0 && (
                  <>
                    <span className="mx-1.5 text-white/30">·</span>
                    {stats.plans} {stats.plans === 1 ? "plano finalizado" : "planos finalizados"}
                  </>
                )}
              </p>
            )}
          </div>
        </section>
      )}

      {isAuthenticated && progress?.href && (
        <section className="max-w-5xl mx-auto section-padding mt-8">
          <div
            data-testid="home-continue-reading"
            role="button"
            tabIndex={0}
            onClick={() => navigate(progress.href)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(progress.href);
              }
            }}
            className="group block bg-white rounded-2xl border border-border shadow-sm card-lift overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/50"
          >
            <div className="flex items-stretch">
              <div className="hidden sm:flex w-16 bg-[hsl(var(--parchment))] items-center justify-center">
                <BookmarkCheck className="w-5 h-5 text-gold" />
              </div>
              <div className="flex-1 p-5 sm:p-6 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    Continuar leitura
                  </p>
                  <h3
                    className="font-display text-lg sm:text-xl text-navy mt-1 leading-tight truncate"
                    data-testid="home-continue-subtitle"
                  >
                    {formatProgressSubtitle(progress)}
                  </h3>
                  <div className="mt-1 flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-navy/55">
                      {labelForType(progress.type)}
                    </span>
                    {hasHistory && (
                      <Link
                        to="/historico"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="home-history-link"
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-navy/60 hover:text-gold transition-colors"
                      >
                        <History className="w-3 h-3" /> Ver histórico
                      </Link>
                    )}
                  </div>
                </div>
                <span
                  className="shrink-0 inline-flex items-center gap-2 bg-navy text-white group-hover:bg-gold group-hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                  data-testid="home-continue-button"
                >
                  Continuar <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {isAuthenticated && activePlan?.plan_slug && (() => {
        const total = activePlan.total_days || 0;
        const done = activePlan.completed_days?.length || 0;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        const target = activePlan.last_href || `/planos/${activePlan.plan_slug}/dia/${activePlan.current_day || 1}`;
        return (
          <section className="max-w-5xl mx-auto section-padding mt-8">
            <div
              role="button"
              tabIndex={0}
              data-testid="home-active-plan"
              onClick={() => navigate(target)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(target);
                }
              }}
              className="group bg-white rounded-2xl border border-border shadow-sm card-lift overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/50"
            >
              <div className="p-5 sm:p-6 flex items-center gap-4">
                <div className="hidden sm:flex w-12 h-12 rounded-xl bg-[hsl(var(--parchment))] items-center justify-center shrink-0">
                  <Calendar className="w-5 h-5 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    Seu plano atual
                  </p>
                  <h3
                    className="font-display text-lg sm:text-xl text-navy mt-1 leading-tight truncate"
                    data-testid="home-active-plan-title"
                  >
                    {activePlan.plan_title || "Plano de leitura"}
                  </h3>
                  <div className="mt-2 flex items-center gap-3">
                    <span
                      className="text-xs text-navy/65"
                      data-testid="home-active-plan-progress-text"
                    >
                      {done} de {total} dias concluídos
                    </span>
                    <span className="text-xs text-navy/40">{pct}%</span>
                  </div>
                  <div className="mt-2 h-1 bg-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gold transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <span
                  className="shrink-0 inline-flex items-center gap-2 bg-navy text-white group-hover:bg-gold group-hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                  data-testid="home-active-plan-button"
                >
                  Continuar plano <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Newsletter removida (spec P0.1 revisto — não faz parte da ordem
          aprovada dos 12 blocos). */}

      {/* Footer spacer */}
      <div className="h-10" />
    </div>
  );
}
